// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import { isThemeActive, Theme, useResizeObserver, useUniqueId } from '@cloudscape-design/component-toolkit/internal';
import InternalIcon from '../icon/internal';
import { getBaseProps } from '../internal/base-component';
import Option from '../internal/components/option';
import { TokenInlineContext } from '../internal/context/token-inline-context';
import LiveRegion from '../live-region/internal';
import Tooltip from '../tooltip/internal.js';
import DismissButton from './dismiss-button';
import { getTokenRootStyles } from './styles';
import legacyTestingStyles from '../token-group/styles.css.js';
import analyticsSelectors from './analytics-metadata/styles.css.js';
import styles from './styles.css.js';
import testUtilStyles from './test-classes/styles.css.js';
function InternalToken({ 
// External
label, ariaLabel, labelTag, description, variant = 'normal', disabled, readOnly, icon, tags, dismissLabel, onDismiss, tooltipContent, 
// Internal
role, disableInnerPadding, __tokenBoxClassName, __customContent, 
// Base
__internalRootRef, ...restProps }) {
    const baseProps = getBaseProps(restProps);
    const tokenRootStyleProps = getTokenRootStyles(restProps.style);
    const labelContainerRef = useRef(null);
    const labelRef = useRef(null);
    const labelTagRef = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const [isEllipsisActive, setIsEllipsisActive] = useState(false);
    const isInline = variant === 'inline';
    const isOneTheme = isThemeActive(Theme.OneTheme);
    // Consumers with their own grouping semantics can pass role="presentation" to treat the root
    // as a pure styling wrapper (strips ARIA and focus/mouse handlers).
    const isPresentation = role === 'presentation';
    const ariaLabelledbyId = useUniqueId();
    const isLabelOverflowing = () => {
        const labelContent = labelRef.current;
        const labelContainer = labelContainerRef.current;
        return !!labelContent && !!labelContainer && labelContent.offsetWidth > labelContainer.offsetWidth;
    };
    const isLabelTagOverflowing = () => {
        const labelTagElement = labelTagRef.current;
        return !!labelTagElement && labelTagElement.scrollWidth > labelTagElement.clientWidth;
    };
    const updateEllipsis = () => {
        if (isInline) {
            setIsEllipsisActive(isLabelOverflowing() || isLabelTagOverflowing());
        }
    };
    useResizeObserver(labelContainerRef, updateEllipsis);
    useResizeObserver(labelTagRef, updateEllipsis);
    const sizedIcon = (iconNode) => {
        var _a;
        const isIconComponent = React.isValidElement(iconNode) &&
            (iconNode.type === InternalIcon || ((_a = iconNode.type) === null || _a === void 0 ? void 0 : _a.displayName) === 'Icon');
        if (isInline && isOneTheme && isIconComponent) {
            return React.cloneElement(iconNode, { size: 'x-small' });
        }
        return iconNode;
    };
    const buildOptionDefinition = () => {
        const isLabelStringOrNumber = typeof label === 'string' || typeof label === 'number';
        const labelObject = isLabelStringOrNumber ? { label: String(label) } : { labelContent: label };
        if (isInline) {
            return {
                ...labelObject,
                disabled,
                labelTag,
                __customIcon: icon && React.createElement("span", { className: clsx(styles.icon, styles['icon-inline']) }, sizedIcon(icon)),
            };
        }
        else {
            return {
                ...labelObject,
                disabled,
                labelTag,
                description,
                tags,
                __customIcon: icon && React.createElement("span", { className: styles.icon }, sizedIcon(icon)),
            };
        }
    };
    // Use span for inline tokens (e.g. inside contentEditable) to avoid block-level elements breaking text flow.
    const SpanOrDivTag = isInline ? 'span' : 'div';
    return (React.createElement(TokenInlineContext.Provider, { value: { isInlineToken: isInline } },
        React.createElement(SpanOrDivTag, { ...baseProps, ref: __internalRootRef, className: clsx(styles.root, legacyTestingStyles.token, testUtilStyles.root, !isInline ? styles['token-normal'] : styles['token-inline'], analyticsSelectors.token, baseProps.className), "aria-label": isPresentation ? undefined : ariaLabel, "aria-labelledby": isPresentation || ariaLabel ? undefined : ariaLabelledbyId, "aria-disabled": isPresentation ? undefined : !!disabled, role: role !== null && role !== void 0 ? role : 'group', onFocus: event => {
                if (!isPresentation) {
                    setShowTooltip(event.target === event.currentTarget);
                }
            }, onBlur: () => {
                if (!isPresentation) {
                    setShowTooltip(false);
                }
            }, onMouseEnter: event => {
                // Don't reopen the tooltip on hover while focus is inside the label.
                const activeElement = document.activeElement;
                if (!isPresentation &&
                    !(activeElement !== event.currentTarget && event.currentTarget.contains(activeElement))) {
                    setShowTooltip(true);
                }
            }, onMouseLeave: () => {
                if (!isPresentation) {
                    setShowTooltip(false);
                }
            }, tabIndex: !isPresentation && !!tooltipContent && isInline && isEllipsisActive ? 0 : undefined },
            React.createElement(SpanOrDivTag, { className: clsx(!isInline ? styles['token-box'] : styles['token-box-inline'], disabled && styles['token-box-disabled'], readOnly && styles['token-box-readonly'], !isInline && !onDismiss && !__customContent && styles['token-box-without-dismiss'], disableInnerPadding && styles['disable-padding'], __tokenBoxClassName), style: tokenRootStyleProps },
                React.createElement(Option, { className: clsx(isInline && onDismiss && styles['token-option-inline-dismissible']), triggerVariant: isInline, singleLineLabelContent: isInline, option: buildOptionDefinition(), disableTitleTooltip: !!tooltipContent, labelContainerRef: labelContainerRef, labelRef: labelRef, labelTagRef: labelTagRef, labelId: ariaLabelledbyId, customContent: __customContent }),
                onDismiss && (React.createElement(DismissButton, { disabled: disabled, dismissLabel: dismissLabel, onDismiss: onDismiss, readOnly: readOnly, inline: isInline }))),
            !!tooltipContent && isInline && isEllipsisActive && showTooltip && (React.createElement(Tooltip, { "data-testid": "token-tooltip", getTrack: () => { var _a, _b, _c; return (_c = (_a = labelContainerRef.current) !== null && _a !== void 0 ? _a : (_b = labelTagRef.current) === null || _b === void 0 ? void 0 : _b.parentElement) !== null && _c !== void 0 ? _c : null; }, content: React.createElement(LiveRegion, null,
                    React.createElement("span", { "data-testid": "tooltip-live-region-content" }, tooltipContent)), onEscape: () => {
                    setShowTooltip(false);
                } })))));
}
export default InternalToken;
//# sourceMappingURL=internal.js.map